import React from 'react';
import PropTypes from 'prop-types';
import { snakeCase } from 'lodash';
import { getImage } from 'gatsby-plugin-image';
import { SingleMessage as SingleMessageBase } from '@comicrelief/component-library';

import { changeTextColor } from '../RichText/utils';
import renderRichText from '../RichText/RichText';
import { paddingOptionMapping } from '../../utils/optionMapping';
import ImagePropType from '../../commonPropTypes/commonPropTypes';

import { InnerCopy, Label } from './SingleMessage.styles';

const SingleMessage = ({
  id,
  title,
  image,
  backgroundColour = 'white',
  text = null,
  label = null,
  copyFirst = false,
  fullImage = false,
  centredText = false,
  secondaryImage = null,
  verticalHeight = false,
  youTubeVideoId = null,
  landscapeVideo = false,
  paddingAbove = null,
  paddingBelow = null,
  paddingAboveSmBreakpointOnly: smPaddingAbove = null,
  paddingBelowSmBreakpointOnly: smPaddingBelow = null

}) => {
  const thisImageData = getImage(image);
  const thisImageLow = thisImageData?.placeholder?.fallback;
  const thisImageFallback = thisImageData?.images?.fallback?.src;
  const thisImageSrcSet = thisImageData?.images?.sources[0]?.srcSet;
  const thisImageDesc = image?.description ?? '';

  const thisSecondaryImageData = getImage(secondaryImage);
  const thisSecondaryImage = thisSecondaryImageData?.images?.fallback?.src;
  const thisSecondaryImageSrcSet = thisSecondaryImageData?.images?.sources[0]?.srcSet;
  const thisSecondaryImageDesc = secondaryImage?.description;

  const thisBgColour = backgroundColour ? snakeCase(backgroundColour) : null;

  // If not set, the prop won't be passed, letting the component use its own default prop values:
  const paddingTop = paddingAbove ? paddingOptionMapping[paddingAbove] : undefined;
  const paddingBottom = paddingBelow ? paddingOptionMapping[paddingBelow] : undefined;
  const smPaddingTop = smPaddingAbove ? paddingOptionMapping[smPaddingAbove] : undefined;
  const smPaddingBottom = smPaddingBelow ? paddingOptionMapping[smPaddingBelow] : undefined;

  return (
    <SingleMessageBase
      key={id}
      title={title}
      backgroundColor={thisBgColour}
      imageLow={thisImageLow}
      imageSet={thisImageSrcSet}
      image={thisImageFallback}
      imageAltText={thisImageDesc}
      imageSet2={thisSecondaryImageSrcSet}
      image2={thisSecondaryImage}
      imageAltText2={thisSecondaryImageDesc}
      copyFirst={copyFirst}
      fullImage={fullImage}
      vhFull={verticalHeight}
      videoID={youTubeVideoId}
      landscapeVideo={landscapeVideo}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      smPaddingTop={smPaddingTop}
      smPaddingBottom={smPaddingBottom}
    >
      {label || text
        ? (
          <InnerCopy centredText={centredText} bgColor={thisBgColour}>
            {label ? (
              <Label
                tag="span"
                size="s"
                color={changeTextColor(thisBgColour)}
              >
                {label}
              </Label>
            ) : null}
            {text && renderRichText(text, thisBgColour, 'singleMessage')}
          </InnerCopy>
        ) : null}
    </SingleMessageBase>
  );
};

SingleMessage.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  label: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  image: ImagePropType,
  secondaryImage: ImagePropType,
  backgroundColour: PropTypes.string,
  centredText: PropTypes.bool,
  copyFirst: PropTypes.bool,
  fullImage: PropTypes.bool,
  verticalHeight: PropTypes.bool,
  youTubeVideoId: PropTypes.string,
  landscapeVideo: PropTypes.bool,
  paddingAbove: PropTypes.string,
  paddingBelow: PropTypes.string,
  paddingAboveSmBreakpointOnly: PropTypes.string,
  paddingBelowSmBreakpointOnly: PropTypes.string
};

export default SingleMessage;
